import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';
import {
    formatCurrencyValue,
    getFullPaymentAmount,
    getPartialPaymentAmount
} from 'ts/common/components/invoice/helpers';
import { useTranslate } from 'ts/common/hooks';
import { type ClientPaymentOption } from 'client_react/common/components/ClientPaymentOptions';
import ClientPaymentSummary from 'client_react/common/components/ClientPaymentSummary';
import { FormFields } from 'client_react/invoice/components/StripeInvoicePaymentForm';

interface Props {
    customAmount?: number;
    paymentAmountType: ClientPaymentOption;
    showTipSection?: boolean;
}

const InvoicePaymentSummary = ({ customAmount, paymentAmountType, showTipSection }: Props) => {
    const { invoice } = useOutletContext<{ invoice: SpApi.Client.IInvoice }>();
    const { watch } = useFormContext<FormFields>();
    const t = useTranslate();

    const tipAmount = watch('tipAmount');

    const totalPaymentAmount =
        paymentAmountType === 'full'
            ? getFullPaymentAmount(invoice)
            : paymentAmountType === 'retainer'
            ? getPartialPaymentAmount(invoice)
            : customAmount ?? 0;
    const totalPrice = formatCurrencyValue(
        totalPaymentAmount + (tipAmount ?? 0),
        invoice.currencyCode
    );

    const discountAmount = Number(invoice.discountTotal);
    const salesTaxAmount = Number(invoice.salesTaxTotal);

    return (
        <ClientPaymentSummary
            showBreakdown={
                (Number(invoice.salesTaxTotal) > 0 || Number(invoice.discountTotal) > 0) &&
                paymentAmountType === 'full' &&
                invoice.dueTotal === invoice.grandTotal
            }
            discounts={
                discountAmount
                    ? formatCurrencyValue(discountAmount, invoice.currencyCode)
                    : undefined
            }
            discountsSubtitle={t('clientInvoice.discounts')}
            salesTax={
                salesTaxAmount
                    ? formatCurrencyValue(salesTaxAmount, invoice.currencyCode)
                    : undefined
            }
            salesTaxSubtitle={t('clientInvoice.paymentSummaryTax')}
            subtotal={formatCurrencyValue(Number(invoice.subtotal), invoice.currencyCode)}
            subtotalSubtitle={t('clientInvoice.paymentSummarySubtotal')}
            tipAmount={
                showTipSection
                    ? formatCurrencyValue(tipAmount ?? 0, invoice.currencyCode)
                    : undefined
            }
            tipAmountSubtitle={showTipSection ? t('tip') : undefined}
            title={t('clientInvoice.paymentSummary')}
            totalPrice={totalPrice}
            totalSubtitle={t('clientInvoice.paymentSummaryTotal')}
        />
    );
};

export default InvoicePaymentSummary;
